import { RootState } from '../../../app/store';
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"
import TonnageModel from '../models/TonnageModel';
import TonnageQueryModel, { QueryItem } from '../models/TonnageQueryModel';
import { OrderDirection } from '../../Datatable/OrderDirection';
import ContactNameModel from '../../ContactSelector/models/ContactNameModel';
import { MatchingType } from '../../MatchingPreview/MatchingConstants';
import TriggerMatchingAreaModel from '../../MatchingPreview/models/TriggerMatchingAreaModel';

export enum SearchType {
    Search,
    Matching
}
interface tonnageSearchState {
    tonnageQuery: TonnageQueryModel,
    tonnageSearchResults: TonnageModel[],
    tonnageContactNames: ContactNameModel[],
    tonnageContactSearchKeyword: string,
    triggerSearch: number,
    datatableSelectedTonnageIndex: number,
    searchResultTotalRecord: number,
    checkedTonnages: TonnageModel[],
    searchType: SearchType,
    triggerFocusTonnageName: number,
    triggerMatchingAreaModel: TriggerMatchingAreaModel<TonnageModel>
}

const initialState: tonnageSearchState = {
    tonnageQuery: {
        vesselNames: [],
        ownerNames: [],
        headOwnerNames: [],
        openPorts: [],
        openAreas: [],
        countries: [],
        dwtFrom: null,
        dwtTo: null,
        updatedDate: 0,
        dateFrom: "",
        dateTo: "",
        ageMax: null,
        classes: [],
        decks: [],
        loaMin: null,
        loaMax: null,
        beamMax: null,
        draftMax: null,
        gearMin: null,
        capacityMin: null,
        grabs: null,
        gearless: null,
        orderBy: "updatedDate",
        orderDirection: OrderDirection.Desc,
    } as TonnageQueryModel,
    tonnageSearchResults: [],
    tonnageContactNames: [],
    tonnageContactSearchKeyword: "",
    triggerSearch: 0,
    datatableSelectedTonnageIndex: -1,
    searchResultTotalRecord: 0,
    checkedTonnages: [],
    searchType: SearchType.Search,
    triggerFocusTonnageName: 0,
    triggerMatchingAreaModel: {
        triggerMatchingArea: 0,
        matchingType: MatchingType.MatchingArea1,
        model: {

        } as TonnageModel
    } as TriggerMatchingAreaModel<TonnageModel>
}

export const TonnageSearchSlice = createSlice({
    name: "TonnageSearch",
    initialState,
    reducers: {
        setVesselNames: (state, action: PayloadAction<QueryItem[]>) => {
            state.tonnageQuery.vesselNames = action.payload;
        },
        setOwnerNames: (state, action: PayloadAction<QueryItem[]>) => {
            state.tonnageQuery.ownerNames = action.payload;
        },
        setHeadOwnerNames: (state, action: PayloadAction<QueryItem[]>) => {
            state.tonnageQuery.headOwnerNames = action.payload;
        },
        setOpenPorts: (state, action: PayloadAction<QueryItem[]>) => {
            state.tonnageQuery.openPorts = action.payload;
        },
        setOpenAreas: (state, action: PayloadAction<QueryItem[]>) => {
            state.tonnageQuery.openAreas = action.payload;
        },
        setCountries: (state, action: PayloadAction<QueryItem[]>) => {
            state.tonnageQuery.countries = action.payload;
        },
        setDwtFrom: (state, action: PayloadAction<number>) => {
            state.tonnageQuery.dwtFrom = action.payload;
        },
        setDwtTo: (state, action: PayloadAction<number>) => {
            state.tonnageQuery.dwtTo = action.payload;
        },
        setUpdatedDate: (state, action: PayloadAction<number>) => {
            state.tonnageQuery.updatedDate = action.payload;
        },
        setDateFrom: (state, action: PayloadAction<string>) => {
            state.tonnageQuery.dateFrom = action.payload;
        },
        setDateTo: (state, action: PayloadAction<string>) => {
            state.tonnageQuery.dateTo = action.payload;
        },
        setAgeMax: (state, action: PayloadAction<number>) => {
            state.tonnageQuery.ageMax = action.payload;
        },
        setClasses: (state, action: PayloadAction<QueryItem[]>) => {
            state.tonnageQuery.classes = action.payload;
        },
        setDecks: (state, action: PayloadAction<QueryItem[]>) => {
            state.tonnageQuery.decks = action.payload;
        },
        setLoaMin: (state, action: PayloadAction<number>) => {
            state.tonnageQuery.loaMin = action.payload;
        },
        setLoaMax: (state, action: PayloadAction<number>) => {
            state.tonnageQuery.loaMax = action.payload;
        },
        setBeamMax: (state, action: PayloadAction<number>) => {
            state.tonnageQuery.beamMax = action.payload;
        },
        setDraftMax: (state, action: PayloadAction<number>) => {
            state.tonnageQuery.draftMax = action.payload;
        },
        setGearMin: (state, action: PayloadAction<number>) => {
            state.tonnageQuery.gearMin = action.payload;
        },
        setCapacityMin: (state, action: PayloadAction<number>) => {
            state.tonnageQuery.capacityMin = action.payload;
        },
        setGrabs: (state, action: PayloadAction<boolean | null>) => {
            state.tonnageQuery.grabs = action.payload;
        },
        setGearLess: (state, action: PayloadAction<boolean | null>) => {
            state.tonnageQuery.gearless = action.payload;
        },
        setOrderBy: (state, action: PayloadAction<string>) => {
            state.tonnageQuery.orderBy = action.payload;
        },
        setOrderDirection: (state, action: PayloadAction<OrderDirection>) => {
            state.tonnageQuery.orderDirection = action.payload;
        },
        setTonnageSearchResults: (state, action: PayloadAction<TonnageModel[]>) => {
            state.tonnageSearchResults = action.payload;
        },
        setTonnageContactNames: (state, action: PayloadAction<ContactNameModel[]>) => {
            state.tonnageContactNames = action.payload;
        },
        setTonnageContactSearchKeyword: (state, action: PayloadAction<string>) => {
            state.tonnageContactSearchKeyword = action.payload;
        },
        setTriggerTonnageSearch: (state, action: PayloadAction<number>) => {
            state.triggerSearch = action.payload;
        },
        setDatatableSelectedTonnageIndex: (state, action: PayloadAction<number>) => {
            state.datatableSelectedTonnageIndex = action.payload;
        },
        setSearchResultTotalRecord: (state, action: PayloadAction<number>) => {
            state.searchResultTotalRecord = action.payload;
        },
        setTonnageQuery: (state, action: PayloadAction<TonnageQueryModel>) => {
            state.tonnageQuery = action.payload;
        },
        resetTonnageSearch: (state) => {
            state.tonnageQuery = initialState.tonnageQuery;
        },
        setCheckedTonnages: (state, action: PayloadAction<TonnageModel[]>) => {
            state.checkedTonnages = action.payload;
        },
        setSearchType: (state, action: PayloadAction<SearchType>) => {
            state.searchType = action.payload;
        },
        setTriggerFocusTonnageName: (state, action: PayloadAction<number>) => {
            state.triggerFocusTonnageName = action.payload;
        },
        setTriggerMatchingAreaModel: (state, action: PayloadAction<TriggerMatchingAreaModel<TonnageModel>>) => {
            state.triggerMatchingAreaModel = action.payload;
        },
    }
});

export const tonnageSearchSelector = createSelector(
    (state: RootState) => state.tonnageSearchSelector,
    (data) => data
);

export const {
    setVesselNames,
    setOwnerNames,
    setHeadOwnerNames,
    setOpenPorts,
    setOpenAreas,
    setCountries,
    setDwtFrom,
    setDwtTo,
    setUpdatedDate,
    setDateFrom,
    setDateTo,
    setAgeMax,
    setClasses,
    setDecks,
    setLoaMin,
    setLoaMax,
    setBeamMax,
    setDraftMax,
    setGearMin,
    setCapacityMin,
    setGrabs,
    setGearLess,
    setOrderBy,
    setOrderDirection,
    setTonnageSearchResults,
    setTonnageContactNames,
    setTonnageContactSearchKeyword,
    setTriggerTonnageSearch,
    setDatatableSelectedTonnageIndex,
    setSearchResultTotalRecord,
    setTonnageQuery,
    resetTonnageSearch,
    setCheckedTonnages,
    setSearchType,
    setTriggerFocusTonnageName,
    setTriggerMatchingAreaModel
} = TonnageSearchSlice.actions;

export default TonnageSearchSlice.reducer;